import * as React from 'react'
import LiveSelect from 'live/components/forms/ui/select'
import styled from 'styled-components'
type Props = {
    className?: string
    placeholder: string
    options: Array<LiveSelectOption>
    selectedOptions: Array<LiveSelectOption>
    disabled?: boolean
    onSelect: (...args: Array<any>) => any
    onRemove: (...args: Array<any>) => any
    selectOption: (arg0: LiveSelectOptionProps) => React.ReactNode
    renderSelectedOption: (arg0: LiveSelectOption) => React.ReactNode
    renderHead?: (arg0: void) => React.ReactNode
    renderTail?: (arg0: void) => React.ReactNode
    creatable?: boolean
    renderSelect: () => React.ReactNode
    renderEntireSelectedOption: () => React.ReactNode
    onChangeGroupChannels?: (newChannels: Array<GridEditorChannelOption>, updateQuery: boolean) => void
}
export default function OptionList({ selectedOptions, onChangeGroupChannels, ...props }: Props) {
    const [draggingIndex, setDraggingIndex] = React.useState<number | null>(null);
    const [dropTargetIndex, setDropTargetIndex] = React.useState<number | null>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);

    const handleDragStart = (e: React.DragEvent, index: number) => {
        setDraggingIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.currentTarget.classList.add('dragging');
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        
        if (!containerRef.current) return;
        
        const container = containerRef.current;
        const items = Array.from(container.getElementsByClassName('drag-item'));
        
        // Obtém a posição Y do mouse
        const mouseY = e.clientY;
        
        // Encontra o item mais próximo baseado na posição Y do mouse
        let targetIndex = items.findIndex((item) => {
            const rect = item.getBoundingClientRect();
            const itemMiddle = rect.top + rect.height / 2;
            return mouseY < itemMiddle;
        });

        // Se não encontrou nenhum item (mouse está abaixo de todos), usa o último índice
        if (targetIndex === -1) {
            targetIndex = items.length;
        }

        setDropTargetIndex(targetIndex);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        const relatedTarget = e.relatedTarget as HTMLElement;
        if (!containerRef.current?.contains(relatedTarget)) {
            setDropTargetIndex(null);
        }
    };

    const handleDrop = (e: React.DragEvent) => {
        e.preventDefault();
        if (draggingIndex === null || dropTargetIndex === null) return;
        
        const newItems = [...selectedOptions];
        const draggedItem = newItems[draggingIndex];
        newItems.splice(draggingIndex, 1);
        
        // Ajusta o índice de destino se necessário
        const adjustedTargetIndex = dropTargetIndex > draggingIndex 
            ? dropTargetIndex - 1 
            : dropTargetIndex;
        
        newItems.splice(adjustedTargetIndex, 0, draggedItem);
        
        setDraggingIndex(null);
        setDropTargetIndex(null);
        
        if (onChangeGroupChannels) {
            onChangeGroupChannels(newItems, true);
        }
    };

    const handleDragEnd = (e: React.DragEvent) => {
        setDraggingIndex(null);
        setDropTargetIndex(null);
        e.currentTarget.classList.remove('dragging');
    };

    const {
        className,
        disabled,
        placeholder,
        options,
        onSelect,
        onRemove,
        selectOption,
        renderSelectedOption,
        renderEntireSelectedOption,
        renderHead,
        renderSelect,
        renderTail,
        creatable
    } = props

    return (
        <OptionListContainer className={`${className || ''} option-list__container`}>
            {renderSelect ? (
                renderSelect()
            ) : (
                <LiveSelect
                    className="option-list__input dropdown-toggler--cancel-drag"
                    groupClassName="option-list__input-container"
                    disabled={disabled}
                    placeholder={placeholder}
                    options={options}
                    onChange={onSelect}
                    creatable={creatable}
                    optionComponent={selectOption}
                />
            )}
            <div 
                ref={containerRef}
                className="option-list__item-container"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
            >
                {renderHead ? renderHead() : null}
                {selectedOptions.map((option: LiveSelectOption, index: number, array: Array<LiveSelectOption>) => (
                    <div
                        key={option.value}
                        draggable
                        onDragStart={(e) => handleDragStart(e, index)}
                        onDragEnd={handleDragEnd}
                        className={`drag-item ${draggingIndex === index ? 'dragging' : ''} ${
                            dropTargetIndex === index ? 'drop-target' : ''
                        }`}
                    >
                        {renderEntireSelectedOption ? (
                            renderEntireSelectedOption({
                                option,
                                index,
                                array,
                                onRemove
                            })
                        ) : (
                            <OptionListItem 
                                key={option.value} 
                                item={option} 
                                onRemove={onRemove} 
                                disabled={disabled}
                            >
                                {renderSelectedOption(option, index, array)}
                            </OptionListItem>
                        )}
                    </div>
                ))}
                {/* Indicador de drop para a última posição */}
                {dropTargetIndex === selectedOptions.length && (
                    <div className="drop-indicator" />
                )}
                {renderTail ? renderTail() : null}
            </div>
        </OptionListContainer>
    )
}

const OptionListContainer = styled.div`
    .Select-option {
        font-size: 12px;
        padding: 5px 8px;
    }
    .form-group {
        margin-bottom: 10px; /* overriding the default margin-bottom */
    }
    .option-list__item-container {
        position: relative;
        min-height: 50px; // Garante altura mínima para quando estiver vazio
    }
    
    .option-list__item-container > div:not(:last-child)::after {
        content: '';
        position: absolute;
        bottom: -8px;
        left: 0;
        right: 0;
        height: 2px;
        background-color: transparent;
        transition: background-color 0.2s ease;
    }
    
    .option-list__item-container > div[data-dragging-over="true"]::after {
        background-color: var(--primary-color);
    }

    .drop-indicator {
        height: 2px;
        background-color: var(--primary-color);
        position: absolute;
        left: 0;
        right: 0;
        transform: scaleX(1);
        transition: transform 0.2s ease;
    }

    .drag-item {
        transition: transform 0.2s ease, opacity 0.2s ease;
        position: relative;
    }

    .drag-item.dragging {
        opacity: 0.5;
        transform: scale(1.02);
    }

    .drag-item.drop-target::before {
        content: '';
        position: absolute;
        top: -2px;
        left: 0;
        right: 0;
        height: 2px;
        background-color: var(--primary-color);
        transform: scaleX(1);
        transition: transform 0.2s ease;
    }

    .option-list__item {
        background: var(--white);
        border-radius: 4px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        transition: all 0.2s ease;
    }

    .option-list__item:hover {
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
    }
`
type ListItemProps = {
    item: LiveSelectOption
    onRemove: (...args: Array<any>) => any
    disabled?: boolean
    children: React.ReactNode
}
export const OptionListItem = ({ children, onRemove, item, disabled }: ListItemProps) => {
    return (
        <ListItemBackground className="option-list__item">
            <ItemContent className="option-list__content">{children}</ItemContent>
            {disabled ? (
                <DisabledListItemSpacer>
                    <i className="far fa-minus-circle option-list__delete-icon" />
                </DisabledListItemSpacer>
            ) : (
                <DeleteButton onClick={() => onRemove(item)} className="option-list__delete-button">
                    <i className="far fa-minus-circle option-list__delete-icon" />
                </DeleteButton>
            )}
        </ListItemBackground>
    )
}
const paddingV = '5px'
const paddingH = '10px'
const ItemContent = styled.div`
    flex: 1 1 auto;
    padding-top: ${paddingV};
    padding-bottom: ${paddingV};
    padding-left: ${paddingH};
`
const deleteButtonDimensions = `
    flex: 0 0 auto;
    margin-left: 5px;
    padding: ${paddingV} ${paddingH};
`
const DisabledListItemSpacer = styled.div`
    visibility: hidden;
    ${deleteButtonDimensions};
`
const DeleteButton = styled.div`
    color: var(--gray-200);
    :hover {
        color: var(--red-500);
    }
    cursor: pointer;
    ${deleteButtonDimensions};
`
const ListItemBackground = styled.div`
    min-height: 30px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 5px;
    cursor: grab;
    transition: background-color 0.2s ease;
    
    &:hover {
        background: var(--gray-50);
    }
    
    &:active {
        cursor: grabbing;
    }
`
